/* eslint-disable react/prop-types */
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import Badge, { BadgeVariant } from 'components/Badge'
import { ButtonPrimary } from 'components/Button'
import { LightCard, OutlineCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CurrencyLogo from 'components/CurrencyLogo'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import FormattedCurrencyAmount from 'components/FormattedCurrencyAmount'
import Loader from 'components/Loader'
import Row, { RowBetween, RowFixed } from 'components/Row'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import TimeBetweenText from 'components/TimeBetweenText'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { BAULT_ROUTER_ADDRESSES } from 'constants/addresses'
import { useTokens } from 'hooks/Tokens'
import { useBaultDatas } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { KodiakFarmStake, useKodiakFarmByPool, useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import usePercentageHandlers from 'hooks/usePercentageHandlers'
import { usePoolUnderlyingBalances } from 'hooks/usePoolPositionDetails'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import { useUSDValue } from 'hooks/useUSDValue'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import WithdrawFromRewardVaultStep from 'pages/Islands/IslandPage/IslandManagementCard/steps/WithdrawFromRewardVaultStep'
import { useContext, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Lock, Unlock } from 'react-feather'
import { Text } from 'rebass'
import styled, { ThemeContext } from 'styled-components'
import { Separator } from 'theme'
import { Abi } from 'viem'
import { useReadContract } from 'wagmi'

import { IslandContextType } from '../context/IslandContextType'
import { Option, Switch } from '../styleds'
import { useOnBaultRedeemCallback, useOnUnstakeCallback, useOnVaultUnstakeCallback } from './hooks/useOnUnstakeCallback'
import WithdrawBalanceConstructorStep from './steps/WithdrawBalanceConstructorStep'
import WithdrawFromBaultStep from './steps/WithdrawFromBaultStep'
import WithdrawFromStakeStep from './steps/WithdrawFromStakeStep'
import { UnstakeManualUnlockButton } from './styleds'

const StyledController = styled(OutlineCard)<{ active?: boolean; pulsing?: boolean; disabled?: boolean }>`
  border-color: ${({ active, theme }) => (active ? theme.deprecated_blue1 : theme.cardBorderPrimary)};
  border-radius: 8px;
  height: 32px;
  width: 32px;

  background: ${({ theme }) => theme.cardPrimary};
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ disabled, theme }) =>
    !disabled
      ? `
    :hover {
      border-color: ${theme.deprecated_blue1};
      background: ${theme.deprecated_blue1};
    }
    cursor: pointer;
  `
      : `
    opacity: 0.5;
  `}

  overflow: hidden;

  user-select: none;
`

const StakedHeaderCard = styled(LightCard)`
  padding: 16px 20px;
  margin-bottom: 8px;
`

// Use a consistent text style
const StakedText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`

type PropsOf<T> = T extends React.ComponentType<infer P> ? P : never

const Controller = ({
  children,
  onMouseDown,
  onMouseUp,
  onPress,
  onClick: _,
  ...props
}: { children: any; onPress: () => void } & PropsOf<typeof StyledController>) => {
  const [pressing, setPressing] = useState(false)

  const handleMouseDown = (e: React.MouseEvent) => {
    setPressing(true)
    return onMouseDown && onMouseDown(e)
  }
  const handleMouseUp = (e: React.MouseEvent) => {
    setPressing(false)
    return onMouseUp && onMouseUp(e)
  }

  useEffect(() => {
    if (pressing) {
      let throttling = 7
      let tick = -1
      let skips = 5

      const int = setInterval(() => {
        if (skips > 0) {
          skips--
          return
        }

        tick++
        if (throttling !== 0 && tick % throttling !== 0) {
          return
        }
        onPress()
        if (throttling > 0) {
          throttling = throttling - 1
        }
      }, 75)

      return () => {
        clearInterval(int)
      }
    }
    return undefined
  }, [pressing, onPress])

  return (
    <StyledController {...props} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onClick={onPress}>
      {children}
    </StyledController>
  )
}

interface StakeProps {
  stake: KodiakFarmStake
  disabled?: boolean
  onSelect?: () => void
  isBaultStake?: boolean
}

const UnstakeView = ({
  islandId: poolAddress,
  rewardVault,
  rewardVaultRewardToken,
  routerAddress,
  poolType,
  poolApiResponse: pool,
  config,
  stakingData: { stakingType, kodiakFarmAddress, baultAddress, rewardVaultAddress, baultFarmAddress },
  showMigrationOption,
}: IslandContextType) => {
  const [value, setValue] = useState('')
  const [stakesUnlocked, setStakesUnlocked] = useState(false)
  const [token, baultToken] = useTokens([poolAddress, baultAddress])
  const theme = useContext(ThemeContext)
  // const { pool } = usePool(poolAddress)
  const kodiakFarm = useKodiakFarmByPool(kodiakFarmAddress)

  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol
  const rewardVaultLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} Reward Vault` : 'Reward Vault'

  const { data: stakesUnlockedData } = useReadContract({
    address: kodiakFarm?.address,
    abi: KODIAK_FARM_ABI as Abi,
    functionName: 'stakesUnlocked',
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!kodiakFarm?.address && stakingType === 'kodiakFarm',
    },
  })

  const [token0, token1] = useTokens([pool?.token0.id, pool?.token1.id])

  const { account, chainId } = useConnection()
  const baultRouterAddress = BAULT_ROUTER_ADDRESSES[chainId]

  const [activePositionType, setActivePositionType] = useState<'rewardVault' | 'bault' | 'stakedBault'>('rewardVault')

  // ======== FARM STATE ========
  const [index, setIndex] = useState(-1)
  const [manualIndexes, setManualIndexes] = useState<number[]>([])
  const [selectedStakes, setSelectedStakes] = useState<KodiakFarmStake[] | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(false)
  const [isRewardVaultModalOpen, setIsRewardVaultModalOpen] = useState(false)
  const [isBaultModalOpen, setIsBaultModalOpen] = useState(false)
  const [isStakedBaultModalOpen, setIsStakedBaultModalOpen] = useState(false)

  // ======== VAULT STATE ========
  const [rewardVaultUnstakeAmount, setRewardVaultUnstakeAmount] = useState('')
  const typedVaultAmount = useMemo(
    () => tryParseCurrencyAmount(rewardVaultUnstakeAmount, token || undefined),
    [rewardVaultUnstakeAmount, token]
  )

  // ======== BAULT STATE ========
  const [baultRedeemAmount, setBaultRedeemAmount] = useState('')
  const typedBaultAmount = useMemo(
    () => tryParseCurrencyAmount(baultRedeemAmount, baultToken || undefined),
    [baultRedeemAmount, baultToken]
  )

  // ======== STAKED BAULT STATE ========
  const [stakedBaultUnstakeAmount, setStakedBaultUnstakeAmount] = useState('')
  const typedStakedBaultAmount = useMemo(
    () => tryParseCurrencyAmount(stakedBaultUnstakeAmount, baultToken || undefined),
    [stakedBaultUnstakeAmount, baultToken]
  )

  // ======== DATA FETCHING ========
  // Farm data
  const { data: stakedResponse, isLoading: isLoadingStakedResponse } = useKodiakFarmLockedStakesOf(
    kodiakFarmAddress ? kodiakFarm : null,
    account
  )

  // Vault data
  const { data: rewardVaultData, isLoading: isLoadingVaultData } = useRewardVaultDatas(
    stakingType === 'rewardVault' ? rewardVault ?? undefined : undefined,
    account
  )

  const { data: baultData, isLoading: isLoadingBaultData } = useBaultDatas(
    stakingType === 'rewardVault' && baultAddress ? baultAddress : undefined,
    account
  )

  const rewardVaultCurrencyPriceData = useGetTokenPrice(poolAddress)
  const rewardVaultCurrencyUSDValue = useUSDValue(rewardVaultUnstakeAmount, rewardVaultCurrencyPriceData.price)

  const baultCurrencyPriceData = useGetTokenPrice(baultAddress)
  const baultCurrencyUSDValue = useUSDValue(baultRedeemAmount, baultCurrencyPriceData.price)

  // For farm stakes only - check if stakes are unlocked
  useEffect(() => {
    if (stakingType === 'kodiakFarm' && stakesUnlockedData !== undefined) {
      setStakesUnlocked(stakesUnlockedData as boolean)
    }
  }, [stakesUnlockedData, stakingType])

  const { regularStakes, baultStakes } = useMemo(() => {
    if (!stakedResponse?.stakes) {
      return { regularStakes: [], baultStakes: [] }
    }

    const regular: KodiakFarmStake[] = []
    const bault: KodiakFarmStake[] = []

    stakedResponse.stakes.forEach((stake) => {
      if (token && stake.liquidity?.currency.equals(token)) {
        regular.push(stake)
      } else if (baultToken && stake.liquidity?.currency.equals(baultToken)) {
        bault.push(stake)
      } else {
        console.warn('Stake does not match any known token, defaulting to regular', stake)
        regular.push(stake)
      }
    })

    return {
      regularStakes: regular,
      baultStakes: bault,
    }
  }, [stakedResponse?.stakes, baultToken, token])

  const hasStakedBaultPositions = useMemo(() => {
    return baultStakes.length > 0
  }, [baultStakes])

  const hasRewardVaultPosition = rewardVaultData?.userStaked && !rewardVaultData.userStaked.equalTo('0')
  const hasAutoCompoundPosition = baultData?.userShares && !baultData.userShares.equalTo('0')
  const hasStakedBaultPosition = hasStakedBaultPositions

  // Count the number of position types present
  const positionTypeCount = [hasRewardVaultPosition, hasAutoCompoundPosition, hasStakedBaultPosition].filter(
    Boolean
  ).length

  const hasBothPositionTypes = useMemo(() => {
    return (
      stakingType === 'kodiakFarm' &&
      baultAddress &&
      rewardVaultData?.userStaked &&
      !rewardVaultData.userStaked.equalTo('0') &&
      baultData?.userShares &&
      !baultData.userShares.equalTo('0')
    )
  }, [stakingType, baultAddress, rewardVaultData?.userStaked, baultData?.userShares])

  const hasBothVaultPositionTypes = useMemo(() => {
    return (
      stakingType === 'rewardVault' &&
      baultAddress &&
      rewardVaultData?.userStaked &&
      !rewardVaultData.userStaked.equalTo('0') &&
      baultData?.userShares &&
      !baultData.userShares.equalTo('0')
    )
  }, [stakingType, baultAddress, rewardVaultData?.userStaked, baultData?.userShares])

  const hasAnyPositions = useMemo(() => {
    if (stakingType === 'kodiakFarm') {
      return regularStakes.length > 0 || baultStakes.length > 0
    } else {
      return (
        (rewardVaultData?.userStaked && !rewardVaultData.userStaked.equalTo('0')) ||
        (baultData?.userShares && !baultData.userShares.equalTo('0')) ||
        hasStakedBaultPositions
      )
    }
  }, [
    stakingType,
    regularStakes,
    baultStakes,
    rewardVaultData?.userStaked,
    baultData?.userShares,
    hasStakedBaultPositions,
  ])

  // Default to vault if user has only vault positions
  useEffect(() => {
    if (stakingType === 'rewardVault') {
      if (hasStakedBaultPositions) {
        setActivePositionType('stakedBault')
      } else if (
        (!baultData?.userShares || baultData.userShares.equalTo('0')) &&
        rewardVaultData?.userStaked &&
        !rewardVaultData.userStaked.equalTo('0')
      ) {
        setActivePositionType('rewardVault')
      } else if (
        (!rewardVaultData?.userStaked || rewardVaultData.userStaked.equalTo('0')) &&
        baultData?.userShares &&
        !baultData.userShares.equalTo('0')
      ) {
        setActivePositionType('bault')
      }
    }
  }, [stakingType, rewardVaultData?.userStaked, baultData?.userShares, hasStakedBaultPositions])

  const { availableRegularStakes, unavailableRegularStakes } = useMemo(() => {
    if (stakesUnlocked) {
      // If stakes are globally unlocked, all stakes are available
      return {
        availableRegularStakes: regularStakes,
        unavailableRegularStakes: [],
      }
    }

    // Otherwise filter based on endingTimestamp
    return {
      availableRegularStakes: regularStakes.filter((stake) => stake.endingTimestamp.getTime() <= Date.now()),
      unavailableRegularStakes: regularStakes.filter((stake) => stake.endingTimestamp.getTime() > Date.now()),
    }
  }, [regularStakes, stakesUnlocked])

  const { availableBaultStakes, unavailableBaultStakes } = useMemo(() => {
    if (stakesUnlocked) {
      // If stakes are globally unlocked, all stakes are available
      return {
        availableBaultStakes: baultStakes,
        unavailableBaultStakes: [],
      }
    }

    // Otherwise filter based on endingTimestamp
    return {
      availableBaultStakes: baultStakes.filter((stake) => stake.endingTimestamp.getTime() <= Date.now()),
      unavailableBaultStakes: baultStakes.filter((stake) => stake.endingTimestamp.getTime() > Date.now()),
    }
  }, [baultStakes, stakesUnlocked])

  const availablePositions = useMemo<KodiakFarmStake[] | undefined>(() => {
    // For vault staking
    if (stakingType === 'rewardVault') {
      if (activePositionType === 'stakedBault') {
        // Return staked bault positions when in that tab
        return availableBaultStakes
      } else if (!rewardVaultData || !rewardVaultData.userStaked || rewardVaultData.userStaked.equalTo('0')) {
        return []
      } else {
        // For vault, create a single position object from the userStaked amount
        return [
          {
            id: `vault-position-${rewardVault}-${account}`,
            kekId: `vault-position-${rewardVault}-${account}`,
            liquidity: rewardVaultData.userStaked,
            endingTimestamp: new Date(0), // No lock time for vault
            lockMultiplier: { toFixed: () => '1.00' }, // No multiplier for vault
          },
        ]
      }
    }

    // For farm staking, return regular LP positions
    return availableRegularStakes
  }, [
    stakingType,
    activePositionType,
    rewardVaultData,
    rewardVault,
    account,
    availableRegularStakes,
    availableBaultStakes,
  ])

  // For farm staking only - track unavailable stakes
  const unavaliableStakes = useMemo<KodiakFarmStake[]>(() => {
    if (stakingType === 'rewardVault' && activePositionType === 'stakedBault') {
      return unavailableBaultStakes
    } else if (stakingType === 'kodiakFarm') {
      return unavailableRegularStakes
    }
    return []
  }, [stakingType, activePositionType, unavailableRegularStakes, unavailableBaultStakes])

  // Total staked amounts
  const totalStakedBaultAmount = useMemo(() => {
    return sumCurrencyAmount(availableBaultStakes.map((stake) => stake.liquidity))
  }, [availableBaultStakes])
  // const unavaliableStakes = useMemo<KodiakFarmStake[]>(() => {
  //   if (stakingType !== 'farm') return []
  //   if (!stakedResponse || !stakedResponse.stakes || stakedResponse.stakes.length === 0) return []

  //   // If stakes are globally unlocked, there are no unavailable stakes
  //   if (stakesUnlocked) return []

  //   return stakedResponse.stakes.filter((item) => item.endingTimestamp.getTime() > Date.now())
  // }, [stakedResponse, stakesUnlocked, stakingType])

  const plus = () => {
    if (!availablePositions) return

    if (index < availablePositions.length - 1) {
      setIndex((prev) => {
        if (prev < availablePositions.length - 1) {
          const newIndex = prev + 1
          return newIndex
        }
        return prev
      })
    }
  }

  const minus = () => {
    if (!availablePositions) return

    if (index - 1 >= -1) {
      setIndex((prev) => {
        const newIndex = prev - 1
        if (newIndex >= -1) {
          return newIndex
        }
        return prev
      })
    }
  }

  const selectedAvailableStakes = useMemo<KodiakFarmStake[]>(() => {
    if (!availablePositions) return []
    const combined = [...availablePositions.slice(0, index + 1), ...manualIndexes.map((idx) => availablePositions[idx])]
    const uniqueArray = combined.filter((item, idx) => {
      const _index = combined.findIndex((i) => i.kekId === item.kekId)
      return _index === idx
    })

    return uniqueArray
  }, [index, availablePositions, manualIndexes])

  const selectedShares = useMemo(() => {
    return sumCurrencyAmount(selectedAvailableStakes.map((item) => item.liquidity))
  }, [selectedAvailableStakes])

  const totalAvailableStakes = useMemo(() => {
    if (!availablePositions) {
      return undefined
    }
    return sumCurrencyAmount(availablePositions.map((item) => item.liquidity))
  }, [availablePositions])

  useEffect(() => {
    if (!selectedShares) {
      setValue('0')
      return
    }
    setValue(selectedShares.toExact() || '0')
  }, [selectedShares])

  // ======== VAULT LOGIC ========
  // Validation for vault unstaking
  const isVaultAmountInvalid = useMemo(() => {
    if (!rewardVaultData?.userStaked || !typedVaultAmount) return false
    return typedVaultAmount.greaterThan(rewardVaultData.userStaked)
  }, [rewardVaultData?.userStaked, typedVaultAmount])

  // ======== BAULT LOGIC ========
  // Validation for bault redemption
  const isBaultAmountInvalid = useMemo(() => {
    if (!baultData?.userShares || !typedBaultAmount) return false
    return typedBaultAmount.greaterThan(baultData.userShares)
  }, [baultData?.userShares, typedBaultAmount])

  // Get estimated LP tokens for bault shares
  const estimatedLpFromBault = useMemo(() => {
    if (!baultData?.userShares || !typedBaultAmount || typedBaultAmount.equalTo('0')) {
      return undefined
    }
    return baultData.estimatedLpTokensForShares(typedBaultAmount)
  }, [baultData, typedBaultAmount])

  // Get underlying token balances for both mechanisms
  const selectedUnderlyingBalances = usePoolUnderlyingBalances(
    pool ? pool : null,
    stakingType === 'rewardVault' && activePositionType === 'rewardVault'
      ? rewardVaultData?.userStaked
      : selectedShares,
    token0,
    token1
  )

  // Get estimated underlying balances for the redeemed amount
  const estimatedTokensForVault = useMemo(() => {
    if (
      !rewardVaultData?.userStaked ||
      rewardVaultData.userStaked.equalTo('0') ||
      !typedVaultAmount ||
      typedVaultAmount.equalTo('0') ||
      !selectedUnderlyingBalances
    ) {
      return undefined
    }

    // Calculate the proportion of tokens based on redeem amount
    const proportion = typedVaultAmount.divide(rewardVaultData.userStaked)

    return {
      amount0: selectedUnderlyingBalances.amount0?.multiply(proportion),
      amount1: selectedUnderlyingBalances.amount1?.multiply(proportion),
    }
  }, [rewardVaultData?.userStaked, typedVaultAmount, selectedUnderlyingBalances])

  // ======== TRANSACTION HOOKS ========
  // Farm unstake callback
  const {
    onConfirmWithdrawStakes: onConfirmFarmWithdraw,
    attemptingTxn: attemptingFarmTxn,
    hash: farmHash,
    willForfeitRewards,
    clear: clearFarm,
  } = useOnUnstakeCallback(stakingType === 'kodiakFarm' ? selectedStakes || [] : [], kodiakFarmAddress, () => {
    setIndex(-1)
    setManualIndexes([])
    setIsOpen(false)
  })

  // Vault unstake callback
  const {
    onConfirmWithdrawStakes: onConfirmVaultWithdraw,
    attemptingTxn: attemptingVaultTxn,
    hash: vaultHash,
    clear: clearVault,
  } = useOnVaultUnstakeCallback(typedVaultAmount, stakingType === 'rewardVault' ? rewardVault : undefined, () => {
    setRewardVaultUnstakeAmount('')
    setIsRewardVaultModalOpen(false)
  })

  // Bault redeem callback - New
  const {
    onConfirmRedeem: onConfirmBaultRedeem,
    attemptingTxn: attemptingBaultTxn,
    hash: baultHash,
    clear: clearBault,
  } = useOnBaultRedeemCallback(typedBaultAmount, baultAddress, baultRouterAddress, account, () => {
    setBaultRedeemAmount('')
    setIsBaultModalOpen(false)
  })

  // Staked Bault unstaking callback
  const {
    onConfirmWithdrawStakes: onConfirmStakedBaultWithdraw,
    attemptingTxn: attemptingStakedBaultTxn,
    hash: stakedBaultHash,
    clear: clearStakedBault,
  } = useOnUnstakeCallback(availableBaultStakes, kodiakFarmAddress, () => {
    setStakedBaultUnstakeAmount('')
    setIsStakedBaultModalOpen(false)
  })

  // ======== STEP COMPONENTS ========
  const constructorStep = useStepWithNext('WithdrawBalanceConstructorStep', WithdrawBalanceConstructorStep, {
    selectedShares,
    userBalanceShares: undefined,
    stakes: availablePositions,
    pool,
    onSelect: ({ stakes }: { stakes: KodiakFarmStake[] }) => setSelectedStakes(stakes),
    defaultActiveStakes: selectedAvailableStakes,
    stakingType,
  })

  // Create specific steps for farm and vault
  const farmWithdrawStep = useStepWithNext('WithdrawFromStakeStep', WithdrawFromStakeStep, {
    stakes: selectedStakes || [],
    kodiakFarm,
    vault: undefined,
    willForfeitRewards,
    onWithdraw: onConfirmFarmWithdraw,
    hash: farmHash,
    attemptingTxn: attemptingFarmTxn,
    pendingText: (
      <>
        Unstaking {selectedShares?.toSignificant(6) || '0'} {token?.symbol}
      </>
    ),
    transactionWaiting: true,
    stakingType: 'farm',
  })

  const rewardVaultWithdrawStep = useStepWithNext('WithdrawFromRewardVaultStep', WithdrawFromRewardVaultStep, {
    amount: typedVaultAmount,
    rewardVault,
    vault: stakingType === 'rewardVault' ? rewardVault : undefined,
    pool: pool ?? null,
    onWithdraw: onConfirmVaultWithdraw,
    hash: vaultHash,
    attemptingTxn: attemptingVaultTxn,
    pendingText: (
      <>
        Unstaking {typedVaultAmount?.toSignificant(6) || '0'} {token?.symbol}
      </>
    ),
    transactionWaiting: true,
    stakingType: 'vault',
  })

  const baultWithdrawStep = useStepWithNext('WithdrawFromBaultStep', WithdrawFromBaultStep, {
    amount: typedBaultAmount,
    baultAddress,
    onRedeem: onConfirmBaultRedeem,
    hash: baultHash,
    attemptingTxn: attemptingBaultTxn,
    pendingText: (
      <>
        Redeeming {typedBaultAmount?.toSignificant(6) || '0'} {baultData?.shareToken?.symbol}
      </>
    ),
    estimatedLp: estimatedLpFromBault,
    exitFee: '0.1%',
    transactionWaiting: true,
  })

  const stakedBaultWithdrawStep = useStepWithNext('WithdrawFromStakeStep', WithdrawFromStakeStep, {
    stakes: availableBaultStakes,
    kodiakFarm,
    vault: undefined,
    willForfeitRewards: false,
    onWithdraw: onConfirmStakedBaultWithdraw,
    hash: stakedBaultHash,
    attemptingTxn: attemptingStakedBaultTxn,
    pendingText: (
      <>
        Unstaking {totalStakedBaultAmount?.toSignificant(6) || '0'} {baultToken?.symbol}
      </>
    ),
    stakingType: 'farm',
    transactionWaiting: true,
  })

  // ======== APPROVE STEP FOR BAULTS ========
  const approveStep = useApproveStep(typedBaultAmount, baultRouterAddress)

  // Reset state when positions change
  useEffect(() => {
    setIndex(-1)
    setManualIndexes([])
  }, [availablePositions?.length])

  useEffect(() => {
    if (stakesUnlocked) {
      setIndex(-1)
      setManualIndexes([])
    }
  }, [stakesUnlocked, stakingType])

  const {
    handleMax: handleVaultMax,
    handle10Percent: handleVault10Percent,
    handle25Percent: handleVault25Percent,
    handle50Percent: handleVault50Percent,
  } = usePercentageHandlers(rewardVaultData?.userStaked, setRewardVaultUnstakeAmount, () => {
    if (rewardVaultData?.userStaked) {
      setRewardVaultUnstakeAmount(rewardVaultData.userStaked.toExact() || '0')
    }
  })

  const {
    handleMax: handleBaultMax,
    handle10Percent: handleBault10Percent,
    handle25Percent: handleBault25Percent,
    handle50Percent: handleBault50Percent,
  } = usePercentageHandlers(baultData?.userShares, setBaultRedeemAmount, () => {
    if (baultData?.userShares) {
      setBaultRedeemAmount(baultData.userShares.toExact() || '0')
    }
  })

  // Loading state
  const isLoading = useMemo(() => {
    if (stakingType === 'kodiakFarm') return isLoadingStakedResponse
    if (stakingType === 'rewardVault') {
      if (activePositionType === 'rewardVault') return isLoadingVaultData
      if (activePositionType === 'bault') return isLoadingBaultData
      if (activePositionType === 'stakedBault') return isLoadingStakedResponse
    }
    return false
  }, [stakingType, activePositionType, isLoadingStakedResponse, isLoadingVaultData, isLoadingBaultData])

  if (isLoading) {
    return (
      <AutoColumn gap="md">
        <Loader />
      </AutoColumn>
    )
  }

  // Stake component for displaying position details
  const Stake = ({ disabled, stake: { liquidity, endingTimestamp, lockMultiplier, kekId }, onSelect }: StakeProps) => {
    const underlyingAmounts = usePoolUnderlyingBalances(pool ? pool : null, liquidity, token0, token1)
    const isLocked = !stakesUnlocked && endingTimestamp.getTime() > Date.now()

    return (
      <RowBetween opacity={disabled ? 0.5 : 1}>
        <RowFixed>
          <DoubleCurrencyLogo currency0={token0 || undefined} currency1={token1 || undefined} size={16} />
          <Text fontSize={12}>
            {underlyingAmounts?.amount0 && (
              <FormattedCurrencyAmount currencyAmount={underlyingAmounts.amount0} significantDigits={6} />
            )}
            /
            {underlyingAmounts?.amount1 && (
              <FormattedCurrencyAmount currencyAmount={underlyingAmounts.amount1} significantDigits={6} />
            )}
          </Text>
          {stakingType === 'kodiakFarm' && (
            <Badge variant={BadgeVariant.PRIMARY} size="sm">
              <Text fontSize={10} opacity={0.5}>
                x{lockMultiplier.toFixed(2)}
              </Text>
            </Badge>
          )}
        </RowFixed>

        <RowFixed>
          {isLocked ? (
            <Lock color={theme.accentFailure} size={14} opacity={0.6} />
          ) : (
            <UnstakeManualUnlockButton clickable={onSelect !== undefined} onClick={() => onSelect?.()}>
              <Unlock color={disabled ? theme.textSecondary : theme.accentSuccess} size={14} opacity={0.6} />
            </UnstakeManualUnlockButton>
          )}
          {isLocked && (
            <Text fontSize={12}>
              <TimeBetweenText date={endingTimestamp}>
                <Loader />
              </TimeBetweenText>
            </Text>
          )}
        </RowFixed>
      </RowBetween>
    )
  }

  // ======== RENDER COMPONENT ========
  // Helper component for consistent UI for staked bault in both contexts
  const StakedBaultUnstakeUI = () => {
    return (
      <>
        <StakedHeaderCard>
          <RowBetween>
            <StakedText color={theme.textSecondary}>Your Staked bault:</StakedText>
            <RowFixed>
              <StakedText>
                {totalStakedBaultAmount ? totalStakedBaultAmount.toSignificant(6) : '0'} {baultToken?.symbol}
              </StakedText>
              <CurrencyLogo currency={baultToken} size="20px" style={{ marginLeft: '8px' }} />
            </RowFixed>
          </RowBetween>
        </StakedHeaderCard>

        {/* Display all staked bault positions */}
        {(unavailableBaultStakes.length > 0 || availableBaultStakes.length > 0) && (
          <LightCard>
            <AutoColumn gap="md">
              {/* Show locked positions first if any */}
              {unavailableBaultStakes.length > 0 && (
                <>
                  {unavailableBaultStakes.map((stake) => (
                    <Stake key={stake.kekId} stake={stake} isBaultStake={true} />
                  ))}
                  {availableBaultStakes.length > 0 && <Separator />}
                </>
              )}

              {/* Show available positions */}
              {availableBaultStakes.map((stake) => (
                <Stake key={stake.kekId} stake={stake} isBaultStake={true} />
              ))}
            </AutoColumn>
          </LightCard>
        )}

        {/* Button for unstaking available bault */}
        <ButtonPrimary disabled={availableBaultStakes.length === 0} onClick={() => setIsStakedBaultModalOpen(true)}>
          {availableBaultStakes.length === 0 ? (
            <>
              <AlertTriangle size={16} color={theme.accentWarning}></AlertTriangle>
              <Text fontSize={14} color={theme.accentWarning}>
                No available bault to unstake
              </Text>
            </>
          ) : (
            <>
              Unstake {totalStakedBaultAmount?.toSignificant(6) || '0'} {baultToken?.symbol}
            </>
          )}
        </ButtonPrimary>
      </>
    )
  }

  // Full return statement
  return (
    <>
      {/* Modals for all unstaking types */}
      <StepLineModal
        steps={[constructorStep, farmWithdrawStep]}
        isOpen={isOpen}
        header={<>Unstake Farm Positions</>}
        onDismiss={clearFarm}
        onFinish={clearFarm}
      />

      <StepLineModal
        steps={[rewardVaultWithdrawStep]}
        isOpen={isRewardVaultModalOpen}
        header={<>Unstake from Vault</>}
        onDismiss={clearVault}
        onFinish={clearVault}
      />

      <StepLineModal
        steps={[approveStep, baultWithdrawStep]}
        isOpen={isBaultModalOpen}
        header={<>Redeem from Auto-Compound</>}
        onDismiss={clearBault}
        onFinish={clearBault}
      />

      <StepLineModal
        steps={[stakedBaultWithdrawStep]}
        isOpen={isStakedBaultModalOpen}
        header={<>Unstake bault from Kodiak Farm</>}
        onDismiss={clearStakedBault}
        onFinish={clearStakedBault}
      />

      {/* Farm Unstaking UI */}
      {stakingType === 'kodiakFarm' && (
        <>
          {/* Display locked positions */}
          {unavaliableStakes.length > 0 && (
            <LightCard>
              <AutoColumn gap="md">
                {unavaliableStakes.map((stake) => (
                  <Stake key={stake.kekId} stake={stake} />
                ))}
                {availablePositions && availablePositions.length > 0 && <Separator />}
                {availablePositions?.map((stake, currentIndex) => (
                  <Stake
                    key={stake.kekId}
                    stake={stake}
                    disabled={!selectedAvailableStakes.some((item) => item.kekId === stake.kekId)}
                    onSelect={() => {
                      if (manualIndexes.includes(currentIndex)) {
                        setManualIndexes((prev) => prev.filter((item) => item !== currentIndex))
                      } else {
                        setManualIndexes((prev) => [...prev, currentIndex])
                      }
                    }}
                  />
                ))}
              </AutoColumn>
            </LightCard>
          )}

          {/* Display only available positions if no locked positions */}
          {unavaliableStakes.length === 0 && availablePositions && availablePositions.length > 0 && (
            <LightCard>
              <AutoColumn gap="md">
                {availablePositions?.map((stake, currentIndex) => (
                  <Stake
                    key={stake.kekId}
                    stake={stake}
                    disabled={!selectedAvailableStakes.some((item) => item.kekId === stake.kekId)}
                    onSelect={() => {
                      if (manualIndexes.includes(currentIndex)) {
                        setManualIndexes((prev) => prev.filter((item) => item !== currentIndex))
                      } else {
                        setManualIndexes((prev) => [...prev, currentIndex])
                      }
                    }}
                  />
                ))}
              </AutoColumn>
            </LightCard>
          )}

          {/* Separator if we have positions */}
          {unavaliableStakes.length > 0 || (availablePositions && availablePositions.length > 0) ? <Separator /> : null}

          <AutoColumn gap="md" style={{ position: 'relative' }}>
            <LightCard opacity={availablePositions?.length ? 1 : 0.5}>
              <RowBetween>
                <RowFixed grow="1" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  <Column>
                    <Row align="end">
                      <Text fontSize={16} color={theme.textPrimary}>
                        {value}
                      </Text>
                      <Text
                        fontSize={16}
                        color={theme.textSecondary}
                        opacity={0.5}
                        hidden={selectedShares ? totalAvailableStakes?.equalTo(selectedShares) : false}
                      >
                        {totalAvailableStakes && <>/{totalAvailableStakes?.toSignificant(6)}</>}
                      </Text>
                    </Row>
                    <Text fontSize={12} color={theme.textSecondary}>
                      {token?.symbol}
                    </Text>
                  </Column>
                </RowFixed>

                <RowFixed grow="0" shrink="0">
                  <Controller disabled={index === -1} onPress={minus}>
                    -
                  </Controller>
                  <Controller disabled={index === (availablePositions?.length || 0) - 1} onPress={plus}>
                    +
                  </Controller>
                </RowFixed>
              </RowBetween>
            </LightCard>

            {availablePositions && availablePositions.length > 0 && (
              <LightCard>
                <AutoColumn gap="md">
                  <RowBetween>
                    <Text fontSize={16} fontWeight={500}>
                      <>Pooled {token0?.symbol}:</>
                    </Text>
                    <RowFixed>
                      <Text fontSize={16} fontWeight={500}>
                        {selectedUnderlyingBalances?.amount0 ? (
                          <FormattedCurrencyAmount
                            significantDigits={6}
                            currencyAmount={selectedUnderlyingBalances?.amount0}
                          />
                        ) : (
                          <>0</>
                        )}
                      </Text>
                      <CurrencyLogo size="20px" currency={token0} />
                    </RowFixed>
                  </RowBetween>
                  <RowBetween>
                    <Text fontSize={16} fontWeight={500}>
                      <>Pooled {token1?.symbol}:</>
                    </Text>
                    <RowFixed>
                      <Text fontSize={16} fontWeight={500}>
                        {selectedUnderlyingBalances?.amount1 ? (
                          <FormattedCurrencyAmount
                            significantDigits={6}
                            currencyAmount={selectedUnderlyingBalances?.amount1}
                          />
                        ) : (
                          <>0</>
                        )}
                      </Text>
                      <CurrencyLogo size="20px" currency={token1} />
                    </RowFixed>
                  </RowBetween>
                </AutoColumn>
              </LightCard>
            )}

            <ButtonPrimary disabled={selectedAvailableStakes.length === 0} onClick={() => setIsOpen(true)}>
              {selectedAvailableStakes.length === 0 ? (
                <>
                  <AlertTriangle size={16} color={theme.accentWarning}></AlertTriangle>
                  <Text fontSize={14} color={theme.accentWarning}>
                    There are no available stakes
                  </Text>
                </>
              ) : (
                <>Unstake {selectedAvailableStakes.length >= 0 && <>{selectedAvailableStakes.length} positions</>}</>
              )}
            </ButtonPrimary>
          </AutoColumn>

          {/* Add bault unstaking section - show when there are any positions */}
          {hasStakedBaultPositions && (
            <AutoColumn gap="md" style={{ marginTop: '16px' }}>
              <StakedBaultUnstakeUI />
            </AutoColumn>
          )}
        </>
      )}

      {/* Vault Unstaking UI */}
      {stakingType === 'rewardVault' && (
        <AutoColumn gap="md">
          {/* Position Type Switcher - only show when there are 2+ position types */}
          {positionTypeCount >= 2 && (
            <Switch style={{ marginBottom: '8px' }}>
              {hasRewardVaultPosition && (
                <Option
                  onClick={() => setActivePositionType('rewardVault')}
                  active={activePositionType === 'rewardVault'}
                  tabIndex={-1}
                >
                  <>Reward Vault</>
                </Option>
              )}
              {hasAutoCompoundPosition && (
                <Option onClick={() => setActivePositionType('bault')} active={activePositionType === 'bault'}>
                  <>Auto-Compound</>
                </Option>
              )}
              {hasStakedBaultPosition && (
                <Option
                  onClick={() => setActivePositionType('stakedBault')}
                  active={activePositionType === 'stakedBault'}
                >
                  <>Staked bault</>
                </Option>
              )}
            </Switch>
          )}

          {/* Reward Vault UI */}
          {activePositionType === 'rewardVault' &&
            rewardVaultData?.userStaked &&
            !rewardVaultData.userStaked.equalTo('0') && (
              <>
                <LightCard>
                  <AutoColumn gap="md">
                    <RowBetween>
                      <RowFixed>
                        <Text fontSize={14} fontWeight={500} color={theme.textSecondary}>
                          Your Staked Balance:
                        </Text>
                      </RowFixed>
                      <RowFixed>
                        <Text fontSize={14} fontWeight={500}>
                          {rewardVaultData.userStaked.toSignificant(6)} {rewardVaultData.userStaked.currency.symbol}
                        </Text>
                        <CurrencyLogo currency={token} size="20px" />
                      </RowFixed>
                    </RowBetween>
                  </AutoColumn>
                </LightCard>

                <CurrencyInputPanel
                  value={rewardVaultUnstakeAmount}
                  onUserInput={setRewardVaultUnstakeAmount}
                  onMax={() => {
                    if (rewardVaultData?.userStaked) {
                      setRewardVaultUnstakeAmount(rewardVaultData.userStaked.toExact() || '0')
                    }
                  }}
                  renderBalance={() => <>{rewardVaultData.userStaked?.toExact()}</>}
                  locked={showMigrationOption}
                  lockedMessage={`Complete migration from Kodiak Farm to ${rewardVaultLabel} to access unstaking`}
                  hideBalance={false}
                  showMaxButton={true}
                  currency={token}
                  id="vault-unstake-amount"
                  showCommonBases
                  label="Amount to Unstake"
                  showPercentButtons={true}
                  percentButtonHandlers={{
                    onSelect10Percent: handleVault10Percent,
                    onSelect25Percent: handleVault25Percent,
                    onSelect50Percent: handleVault50Percent,
                    onSelectMax: handleVaultMax,
                  }}
                  apiUSDValue={rewardVaultCurrencyUSDValue}
                />

                {typedVaultAmount && !typedVaultAmount.equalTo('0') && !isVaultAmountInvalid && (
                  <LightCard>
                    <AutoColumn gap="md">
                      <RowBetween>
                        <Text fontSize={16} fontWeight={500}>
                          Pooled {token0?.symbol}:
                        </Text>
                        <RowFixed>
                          <Text fontSize={16} fontWeight={500}>
                            {estimatedTokensForVault?.amount0 ? (
                              <FormattedCurrencyAmount
                                significantDigits={6}
                                currencyAmount={estimatedTokensForVault.amount0}
                              />
                            ) : (
                              <>0</>
                            )}
                          </Text>
                          <CurrencyLogo size="20px" currency={token0} />
                        </RowFixed>
                      </RowBetween>
                      <RowBetween>
                        <Text fontSize={16} fontWeight={500}>
                          Pooled {token1?.symbol}:
                        </Text>
                        <RowFixed>
                          <Text fontSize={16} fontWeight={500}>
                            {estimatedTokensForVault?.amount1 ? (
                              <FormattedCurrencyAmount
                                significantDigits={6}
                                currencyAmount={estimatedTokensForVault.amount1}
                              />
                            ) : (
                              <>0</>
                            )}
                          </Text>
                          <CurrencyLogo size="20px" currency={token1} />
                        </RowFixed>
                      </RowBetween>
                    </AutoColumn>
                  </LightCard>
                )}

                <ButtonPrimary
                  disabled={!typedVaultAmount || typedVaultAmount.equalTo('0') || isVaultAmountInvalid}
                  onClick={() => setIsRewardVaultModalOpen(true)}
                >
                  {isVaultAmountInvalid ? (
                    'Insufficient staked amount'
                  ) : !typedVaultAmount || typedVaultAmount.equalTo('0') ? (
                    'Enter unstake amount'
                  ) : (
                    <>
                      Unstake {typedVaultAmount.toSignificant(6)} {token?.symbol}
                    </>
                  )}
                </ButtonPrimary>
              </>
            )}

          {/* Bault UI */}
          {activePositionType === 'bault' && baultData?.userShares && !baultData.userShares.equalTo('0') && (
            <>
              <LightCard>
                <AutoColumn gap="md">
                  <RowBetween>
                    <RowFixed>
                      <Text fontSize={14} fontWeight={500} color={theme.textSecondary}>
                        Your Bault Shares:
                      </Text>
                    </RowFixed>
                    <RowFixed>
                      <Text fontSize={14} fontWeight={500}>
                        {baultData.userShares.toSignificant(6)} {baultData.shareToken?.symbol}
                      </Text>
                      <CurrencyLogo currency={baultData.shareToken} size="20px" />
                    </RowFixed>
                  </RowBetween>
                  <Text fontSize={12} color={theme.textSecondary} textAlign="right">
                    ≈ {baultData.estimatedLpTokens?.toSignificant(6) || '0'} {baultData.underlyingToken?.symbol}
                  </Text>
                </AutoColumn>
              </LightCard>

              <CurrencyInputPanel
                value={baultRedeemAmount}
                onUserInput={setBaultRedeemAmount}
                onMax={() => {
                  if (baultData?.userShares) {
                    setBaultRedeemAmount(baultData.userShares.toExact() || '0')
                  }
                }}
                renderBalance={() => <>{baultData.userShares?.toExact()}</>}
                hideBalance={false}
                showMaxButton={true}
                currency={baultData.shareToken}
                id="bault-redeem-amount"
                showCommonBases
                label="Amount to Redeem"
                showPercentButtons={true}
                percentButtonHandlers={{
                  onSelect10Percent: handleBault10Percent,
                  onSelect25Percent: handleBault25Percent,
                  onSelect50Percent: handleBault50Percent,
                  onSelectMax: handleBaultMax,
                }}
                apiUSDValue={baultCurrencyUSDValue}
              />

              {typedBaultAmount && !typedBaultAmount.equalTo('0') && !isBaultAmountInvalid && (
                <LightCard>
                  <AutoColumn gap="md">
                    <RowBetween>
                      <Text fontSize={16} fontWeight={500}>
                        You will receive:
                        <br />
                        <Text fontSize={14} color={theme.textSecondary} fontWeight={500}>
                          (estimated)
                        </Text>
                      </Text>
                      <RowBetween width="auto" gap="12px">
                        <Text fontSize={16} fontWeight={500}>
                          {estimatedLpFromBault ? (
                            <FormattedCurrencyAmount significantDigits={5} currencyAmount={estimatedLpFromBault} />
                          ) : (
                            <>0</>
                          )}
                        </Text>
                        <DoubleCurrencyLogo currency0={token0 || undefined} currency1={token1 || undefined} size={20} />
                      </RowBetween>
                    </RowBetween>
                  </AutoColumn>
                </LightCard>
              )}

              <ButtonPrimary
                disabled={!typedBaultAmount || typedBaultAmount.equalTo('0') || isBaultAmountInvalid}
                onClick={() => setIsBaultModalOpen(true)}
              >
                {isBaultAmountInvalid ? (
                  'Insufficient auto-compound shares'
                ) : !typedBaultAmount || typedBaultAmount.equalTo('0') ? (
                  'Enter redemption amount'
                ) : (
                  <>
                    Redeem {typedBaultAmount.toSignificant(6)} {baultData.shareToken?.symbol}
                  </>
                )}
              </ButtonPrimary>
            </>
          )}

          {/* Staked bault UI - use the same component as in farm context */}
          {activePositionType === 'stakedBault' && hasStakedBaultPositions && <StakedBaultUnstakeUI />}

          {/* No positions message */}
          {!hasAnyPositions && (
            <LightCard>
              <Text fontSize={16} textAlign="center" color={theme.textSecondary}>
                You don&apos;t have any positions to unstake.
              </Text>
            </LightCard>
          )}
        </AutoColumn>
      )}
    </>
  )
}

export default UnstakeView
